// Calendar day view for scheduling dashboard - shows bookings in a timeline
import React, { useState, useEffect } from 'react';
import { getSchedulingDashboard } from '../../services/scheduling-api';
import { Booking } from '../../types/scheduling';

const HOUR_HEIGHT = 60; // pixels per hour
const START_HOUR = 7;   // 7:00 AM
const END_HOUR = 21;    // 9:00 PM
const TOTAL_HOURS = END_HOUR - START_HOUR;

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  confirmed: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  cancelled: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  pending: { bg: '#fef3c7', text: '#854d0e', border: '#fde68a' },
  completed: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  rescheduled: { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' },
  no_show: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
};

const formatDateLabel = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatHour = (hour: number): string => {
  if (hour === 0 || hour === 12) {
    return hour === 0 ? '12:00 AM' : '12:00 PM';
  }
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const isSameDay = (dateString: string, targetDate: Date): boolean => {
  const date = new Date(dateString);
  return (
    date.getFullYear() === targetDate.getFullYear() &&
    date.getMonth() === targetDate.getMonth() &&
    date.getDate() === targetDate.getDate()
  );
};

const isToday = (date: Date): boolean => {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const getBookingPosition = (booking: Booking): { top: number; height: number } => {
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);

  const startHours = start.getHours() + start.getMinutes() / 60;
  const endHours = end.getHours() + end.getMinutes() / 60;

  const top = (startHours - START_HOUR) * HOUR_HEIGHT;
  const height = Math.max((endHours - startHours) * HOUR_HEIGHT, 30); // minimum 30px

  return { top, height };
};

const SchedulingCalendarView: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSchedulingDashboard();
      if (data.needsSetup) {
        setError('Please complete scheduling setup first.');
        return;
      }
      setAllBookings(data.upcomingBookings || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
  };

  const goToNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const dayBookings = allBookings.filter((b) => isSameDay(b.startTime, selectedDate));

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <p>Loading calendar...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="cal-view-root">
      {/* Responsive Styles - base classes set desktop defaults, media queries override for mobile */}
      <style>{`
        /* ---- Desktop defaults (properties that change at breakpoints) ---- */
        .cal-date-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
        }
        .cal-nav-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .cal-nav-btn {
          padding: 8px 12px;
        }
        .cal-today-btn {
          padding: 8px 16px;
        }
        .cal-date-label {
          font-size: 1.25rem;
        }
        .cal-hour-col {
          width: 80px;
        }
        .cal-hour-label {
          padding-right: 12px;
          font-size: 0.75rem;
        }
        .cal-booking-card {
          left: 8px;
          right: 8px;
          padding: 8px 12px;
        }
        .cal-booking-inner {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
        }
        .cal-booking-name {
          font-size: 0.875rem;
        }
        .cal-booking-time {
          font-size: 0.75rem;
        }
        .cal-booking-event-type {
          font-size: 0.75rem;
        }
        .cal-status-badge {
          font-size: 0.7rem;
        }
        .cal-join-link {
          font-size: 0.7rem;
        }
        .cal-empty-msg {
          padding: 60px 20px;
        }

        /* ---- Tablet (max-width: 768px) ---- */
        @media (max-width: 768px) {
          .cal-view-root {
            padding: 0 4px;
          }
          .cal-date-nav {
            flex-wrap: wrap;
            gap: 8px;
            padding: 12px 14px;
          }
          .cal-date-label {
            font-size: 1.05rem;
          }
          .cal-hour-col {
            width: 60px;
          }
          .cal-hour-label {
            padding-right: 8px;
            font-size: 0.7rem;
          }
          .cal-booking-card {
            left: 4px;
            right: 4px;
            padding: 6px 8px;
          }
          .cal-booking-name {
            font-size: 0.8rem;
          }
          .cal-nav-btn {
            min-width: 44px;
            min-height: 44px;
          }
          .cal-today-btn {
            min-height: 44px;
          }
        }

        /* ---- Phone (max-width: 480px) ---- */
        @media (max-width: 480px) {
          .cal-date-nav {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            padding: 10px 10px;
          }
          .cal-nav-row {
            justify-content: center;
          }
          .cal-date-label {
            font-size: 0.95rem;
            text-align: center;
            order: 2;
          }
          .cal-appt-count {
            text-align: center;
            order: 3;
          }
          .cal-hour-col {
            width: 40px;
          }
          .cal-hour-label {
            padding-right: 4px;
            font-size: 0.65rem;
          }
          .cal-booking-card {
            left: 2px;
            right: 2px;
            padding: 6px 6px;
            min-height: 44px;
          }
          .cal-booking-name {
            font-size: 0.75rem;
          }
          .cal-booking-time {
            font-size: 0.65rem;
          }
          .cal-booking-event-type {
            font-size: 0.65rem;
          }
          .cal-booking-inner {
            flex-direction: column;
            gap: 4px;
          }
          .cal-booking-badges {
            align-self: flex-start;
          }
          .cal-status-badge {
            font-size: 0.6rem;
          }
          .cal-join-link {
            font-size: 0.6rem;
          }
          .cal-empty-msg {
            padding: 40px 12px;
          }
        }
      `}</style>

      {/* Date Navigation */}
      <div
        className="cal-date-nav"
        style={{
          marginBottom: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
        }}
      >
        <div className="cal-nav-row">
          <button
            className="cal-nav-btn"
            onClick={goToPreviousDay}
            style={{
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: '1',
            }}
          >
            &#8249;
          </button>
          <button
            className="cal-today-btn"
            onClick={goToToday}
            style={{
              backgroundColor: isToday(selectedDate) ? '#3b82f6' : '#f3f4f6',
              color: isToday(selectedDate) ? 'white' : '#374151',
              border: isToday(selectedDate) ? '1px solid #3b82f6' : '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem',
            }}
          >
            Today
          </button>
          <button
            className="cal-nav-btn"
            onClick={goToNextDay}
            style={{
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: '1',
            }}
          >
            &#8250;
          </button>
        </div>

        <h2 className="cal-date-label" style={{ margin: 0, fontWeight: '600' }}>
          {formatDateLabel(selectedDate)}
        </h2>

        <div className="cal-appt-count" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {dayBookings.length} appointment{dayBookings.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Timeline */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        {dayBookings.length === 0 ? (
          <div className="cal-empty-msg" style={{ textAlign: 'center', color: '#6b7280' }}>
            <p style={{ fontSize: '1.1rem', margin: '0 0 8px' }}>No appointments today</p>
            <p style={{ fontSize: '0.875rem', margin: 0 }}>
              Use the arrows to navigate to other days.
            </p>
          </div>
        ) : (
          <div style={{ position: 'relative', display: 'flex' }}>
            {/* Hour Labels Column */}
            <div
              className="cal-hour-col"
              style={{
                flexShrink: 0,
                borderRight: '1px solid #e5e7eb',
              }}
            >
              {Array.from({ length: TOTAL_HOURS }, (_, i) => {
                const hour = START_HOUR + i;
                return (
                  <div
                    key={hour}
                    className="cal-hour-label"
                    style={{
                      height: `${HOUR_HEIGHT}px`,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                      paddingTop: '4px',
                      color: '#9ca3af',
                      borderBottom: '1px solid #f3f4f6',
                    }}
                  >
                    {formatHour(hour)}
                  </div>
                );
              })}
            </div>

            {/* Bookings Column */}
            <div
              style={{
                flex: 1,
                position: 'relative',
                height: `${TOTAL_HOURS * HOUR_HEIGHT}px`,
              }}
            >
              {/* Hour gridlines */}
              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    top: `${i * HOUR_HEIGHT}px`,
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#f3f4f6',
                  }}
                />
              ))}

              {/* Current time indicator */}
              {isToday(selectedDate) && (() => {
                const now = new Date();
                const nowHours = now.getHours() + now.getMinutes() / 60;
                if (nowHours >= START_HOUR && nowHours <= END_HOUR) {
                  const topPos = (nowHours - START_HOUR) * HOUR_HEIGHT;
                  return (
                    <div
                      style={{
                        position: 'absolute',
                        top: `${topPos}px`,
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#ef4444',
                        zIndex: 10,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: '-4px',
                          top: '-4px',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ef4444',
                        }}
                      />
                    </div>
                  );
                }
                return null;
              })()}

              {/* Booking Cards */}
              {dayBookings.map((booking) => {
                const { top, height } = getBookingPosition(booking);
                const colors = STATUS_COLORS[booking.status] || STATUS_COLORS.confirmed;

                return (
                  <div
                    key={booking.id}
                    className="cal-booking-card"
                    style={{
                      position: 'absolute',
                      top: `${top}px`,
                      height: `${height - 4}px`,
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'default',
                      zIndex: 5,
                    }}
                  >
                    <div className="cal-booking-inner">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p
                          className="cal-booking-name"
                          style={{
                            margin: 0,
                            fontWeight: '600',
                            color: '#111827',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {booking.inviteeName}
                        </p>
                        <p
                          className="cal-booking-time"
                          style={{
                            margin: '2px 0 0',
                            color: '#6b7280',
                          }}
                        >
                          {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                        </p>
                        {booking.eventType?.title && (
                          <p
                            className="cal-booking-event-type"
                            style={{
                              margin: '2px 0 0',
                              color: '#6b7280',
                            }}
                          >
                            {booking.eventType.title}
                          </p>
                        )}
                      </div>

                      <div className="cal-booking-badges" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <span
                          className="cal-status-badge"
                          style={{
                            padding: '2px 6px',
                            backgroundColor: colors.bg,
                            color: colors.text,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            fontWeight: '500',
                            textTransform: 'capitalize',
                          }}
                        >
                          {booking.status}
                        </span>
                        {booking.meetLink && (
                          <a
                            className="cal-join-link"
                            href={booking.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '2px 8px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '4px',
                              fontWeight: '500',
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Join
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulingCalendarView;
